import { LitElement, html } from 'lit';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../../shared/services/debug.service.js';
import { TextOptInService } from '../../../../../services/text-opt-in.service.js';
import { globalStyle } from '../../../../../global-styles.style.js';
import { validateForm } from '../../../../../shared/decorators/validate-form.decorator.js';
import '@cof/fs-phone-number-field-wc/fs-phone-number-field.js';
import { stratum } from '../../../../../../constants/stratum.js';

class RefiPersonalTextOptInComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepCommunication');
    this.validity = {};
    this.phonesMatch = true;
    this.showCustomerContactErrorMsg = false;
    this.showAlreadyAssociatedErrorMsg = false;
    this.optInMissing = false;
    this.formSubmitted = false;
  }

  static properties = {
    applicant: { type: Object, reflect: true },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    prefix: { type: String, attribute: true },
    phoneType: { type: String, attribute: 'phone-type', reflect: true },
    phoneNumber: { type: String, attribute: 'phone-number', reflect: true },
    phoneConfirmNumber: {
      type: String,
      reflect: true,
    },
    smsOptIn: { type: Boolean, reflect: true },
    phonesMatch: { type: Boolean, reflect: true },
    showCustomerContactErrorMsg: { type: Boolean, reflect: true },
    showAlreadyAssociatedErrorMsg: { type: Boolean, reflect: true },
    optInMissing: { type: Boolean, reflect: true },
    open: { type: Boolean, attribute: true, reflect: true },
    formSubmitted: { type: Boolean, reflect: true },
  };

  static styles = [globalStyle];

  connectedCallback() {
    super.connectedCallback();
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.optInPhoneId = `${this.coAppPrefix}optInPhone`;
    this.optInPhoneConfirmId = `${this.coAppPrefix}optInPhoneConfirm`;
  }

  update(changedProperties) {
    super.update(changedProperties);
    if (changedProperties.has('open') && this.open) {
      this.openModal();
    }
  }

  render() {
    return html`
            <fs-modal-dialog
                    id="smsOptInModal"
                    accessible-title="smsOptInModal"
                    dismissible
                    size="small"
                    @modal-dialog-close="${() => {
                      if (!this.formSubmitted) {
                        stratum.publishTag(
                          TagKey.MOBILE_NUMBER_MODULE_PHONE_MODAL_DISMISS
                        );
                      }
                      trackAnalyticsEvent('textmodal:cancel');
                      this.dispatchEvent(
                        new CustomEvent('modal-closed', {
                          detail: {
                            data: {
                              phoneNumber: this.phoneNumber,
                              smsOptIn: this.formSubmitted
                                ? this.smsOptIn
                                : false,
                            },
                          },
                        })
                      );
                    }}"
            >
                <refi-heading slot="modal-dialog-header">
                    Confirm your ${this.phoneType} number
                </refi-heading>
                <section
                        class="fs-body-text fs-body-text--normal"
                        slot="modal-dialog-body"
                >
                    <fs-body-text size="normal" variant="primary">
                        <span ?hidden="${
                          this.phoneType !== 'mobile'
                        }">Please enter your mobile number to receive texts about your Auto Refinance application.</span>
                        The ${
                          this.phoneType
                        } phone number you provide here will be updated across all your Capital One accounts.
                    </fs-body-text>

                    <form
                            id="sms-opt-in-form"
                            class="sms-opt-in-form"
                            @submit="${this.onSubmit}"
                            novalidate
                    >
                        <div class="personal__optInPhone">
                            <fs-phone-number-field
                                    id="${this.optInPhoneId}"
                                    autocomplete="tel"
                                    name="phone"
                                    type="tel"
                                    value-missing-message="Please enter a valid phone number"
                                    maxLength="14"
                                    pattern="^([2-9]\\d{2})-?[2-9]\\d{2}-?\\d{4}$"
                                    required
                                    @change="${(ev) => {
                                      this.validity[this.optInPhoneId] =
                                        ev.target.validity.valid;
                                      if (ev.target.value.startsWith('1-')) {
                                        ev.target.value =
                                          ev.target.value.slice(2);
                                      }
                                      this.phoneNumber = ev.target.value;
                                    }}"
                                    @blur="${(ev) => {
                                      this.validity[this.optInPhoneId] =
                                        ev.target.validity.valid;
                                      this.comparePhones(true);
                                      if (ev.target.validity.valid) {
                                        stratum.publishTag(
                                          TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                                          {
                                            replacements: {
                                              value: 'optInPhoneNumber',
                                            },
                                          }
                                        );
                                      } else {
                                        stratum.publishTag(
                                          TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                                          {
                                            replacements: {
                                              value:
                                                'Please enter a valid phone number XXX-XXX-XXXX or XXXXXXXXXX',
                                            },
                                          }
                                        );
                                      }
                                    }}"
                                    value="${this.phoneNumber}"
                                    pattern-mismatch-message="Please enter a valid phone number XXX-XXX-XXXX or XXXXXXXXXX"
                            >
                                Phone number
                            </fs-phone-number-field>
                            <fs-phone-number-field
                                    id="${this.optInPhoneConfirmId}"
                                    name="phone"
                                    type="tel"
                                    value-missing-message="Please enter a valid phone number"
                                    maxLength="14"
                                    pattern="^([2-9]\\d{2})-?[2-9]\\d{2}-?\\d{4}$"
                                    required
                                    @change="${(ev) => {
                                      this.validity[this.optInPhoneConfirmId] =
                                        ev.target.validity.valid;
                                      if (ev.target.value.startsWith('1-')) {
                                        ev.target.value =
                                          ev.target.value.slice(2);
                                      }
                                      this.phoneConfirmNumber = ev.target.value;
                                      trackAnalyticsEvent(
                                        'textmodal:confirm mobile num'
                                      );
                                    }}"
                                    @blur="${(ev) => {
                                      this.validity[this.optInPhoneConfirmId] =
                                        ev.target.validity.valid;
                                      this.comparePhones(true);
                                      if (ev.target.validity.valid) {
                                        stratum.publishTag(
                                          TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD,
                                          {
                                            replacements: {
                                              value: 'optInPhoneNumberConfirm',
                                            },
                                          }
                                        );
                                      } else {
                                        stratum.publishTag(
                                          TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                                          {
                                            replacements: {
                                              value:
                                                'Please enter a valid phone number XXX-XXX-XXXX or XXXXXXXXXX',
                                            },
                                          }
                                        );
                                      }
                                    }}"
                                    value="${this.phoneConfirmNumber}"
                                    pattern-mismatch-message="Please enter a valid phone number XXX-XXX-XXXX or XXXXXXXXXX"
                            >
                                Confirm your phone number
                            </fs-phone-number-field>
                            <fs-body-text
                                    size="small"
                                    variant="feedback-negative"
                                    ?hidden="${this.phonesMatch}"
                                    id="${`${this.optInPhoneConfirmId}-phoneMismatch`}"
                            >
                                Please make sure the phone numbers match
                            </fs-body-text>

                            <div role="radiogroup" aria-labelledby="smsOptInChoice">
                                <fs-radio
                                        id="smsOptInChoiceYes"
                                        name="smsOptInChoiceYes"
                                        value="true"
                                        required
                                        ?checked="${this.smsOptIn}"
                                        @change="${() => {
                                          this.smsOptIn = true;
                                          this.optInMissing = false;
                                          this.validity.optInRadio = true;
                                        }}"
                                >I want to receive automated texts
                                </fs-radio>
                                <fs-body-text size="small" ?hidden="${!this
                                  .smsOptIn}">
                                    By providing your phone number, you consent to receive automated,
                                    pre-recorded or artificial calls, including text messages, relating to your
                                    relationship with Capital One.
                                    You understand these calls or text messages may relate to the handling, servicing,
                                    and billing for any
                                    Capital One account you may have. Message and Data rates may apply. You will be sent
                                    a confirmation
                                    message to your mobile device. Reply "STOP" to end further messages.
                                </fs-body-text>
                                <fs-radio
                                        id="smsOptInChoiceNo"
                                        name="smsOptInChoiceNo"
                                        value="false"
                                        required
                                        ?checked="${this.smsOptIn === false}"
                                        @change="${() => {
                                          this.smsOptIn = false;
                                          this.optInMissing = false;
                                          this.validity.optInRadio = true;
                                        }}"
                                >Only contact me through non-automated means
                                </fs-radio>
                                <fs-body-text size="small" variant="feedback-negative" ?hidden="${
                                  this.smsOptIn || this.smsOptIn === undefined
                                }">
                                    Keep in mind we won’t be able to text you updates about your application if you
                                    choose this option.
                                </fs-body-text>
                                <fs-body-text size="small" ?hidden="${
                                  this.smsOptIn || this.smsOptIn === undefined
                                }">
                                    When you disable text messages or calls sent by automated
                                    means, for your security, we may still contact you in the event of fraud, identity
                                    theft, data breaches,
                                    or money transfers. You can stop these text messages by replying "STOP".
                                </fs-body-text>
                                <fs-body-text
                                        size="small"
                                        variant="feedback-negative"
                                        ?hidden="${!this.optInMissing}"
                                        id="optInMissing"
                                >
                                    Please select an option
                                </fs-body-text>
                            </div>
                            <fs-body-text
                                    size="small"
                                    variant="feedback-negative"
                                    ?hidden="${!this
                                      .showAlreadyAssociatedErrorMsg}"
                            >
                                This mobile number is currently opted in to receive alerts for another customer account. Please either provide another number or designate this number as home or work.
                            </fs-body-text>
                            <fs-body-text
                                    size="small"
                                    variant="feedback-negative"
                                    ?hidden="${!this
                                      .showCustomerContactErrorMsg}"
                            >
                                There was a technical issue. Please try again later.
                            </fs-body-text>
                            <fs-modal-dialog-footer slot="modal-dialog-footer">
                                <fs-button type="submit" class="fs-button fs-button--primary">Save</fs-button>
                            </fs-modal-dialog-footer>
                    </form>
                </section>
            </fs-modal-dialog>
        `;
  }

  openModal() {
    this.smsOptIn = undefined;
    const phoneField = this.shadowRoot.getElementById(this.optInPhoneId);
    const phoneConfirmField = this.shadowRoot.getElementById(
      this.optInPhoneConfirmId
    );
    this.validity.optInRadio = false;
    this.validity[this.optInPhoneId] =
      phoneField.validity.valid && phoneField.value !== '';
    this.validity[this.optInPhoneConfirmId] =
      phoneConfirmField.validity.valid && phoneConfirmField.value !== '';
    this.shadowRoot.getElementById('smsOptInModal').openModalDialog();
    stratum.publishTag(TagKey.MOBILE_NUMBER_MODULE_CONFIRM_MOBILE_NUMBER_MODAL);
  }

  @validateForm()
  async onSubmit() {
    let valid = true;
    this.debug.log(this.validity);
    if (!this.validity.optInRadio) {
      this.optInMissing = true;
    }
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    if (valid) {
      this.debug.log('valid');
      try {
        await TextOptInService.saveTextOptIn(
          this.phoneNumber,
          this.smsOptIn,
          this.phoneType
        );
        this.formSubmitted = true;
        stratum.publishTag(
          TagKey.MOBILE_NUMBER_MODULE_SMS_OPT_PHONE_CHANGE_MODAL,
          { replacements: { value: this.smsOptIn } }
        );
        stratum.publishTag(TagKey.MOBILE_NUMBER_MODULE_PHONE_MODAL_SAVE);
        this.shadowRoot.getElementById('smsOptInModal').closeModalDialog();
      } catch (e) {
        stratum.publishTag(
          TagKey.MOBILE_NUMBER_MODULE_SMS_OPT_SELECTION_FAILURE
        );
        try {
          const errorMessage = await e.json();
          this.debug.log('customer contact had an error: ', errorMessage);
          if (errorMessage?.id === 5001) {
            this.showAlreadyAssociatedErrorMsg = true;
            stratum.publishTag(
              TagKey.MOBILE_NUMBER_MODULE_TCPA_ALREADY_ASSOCIATED_ERROR_MESSAGE
            );
          } else {
            this.showCustomerContactErrorMsg = true;
          }
        } catch (nonHttpResponseError) {
          this.debug.log(
            'customer contact had an error: ',
            nonHttpResponseError
          );
          this.showCustomerContactErrorMsg = true;
        }
      }
    }
  }

  comparePhones() {
    const phoneField = this.shadowRoot.getElementById(this.optInPhoneId);
    const confirmPhoneField = this.shadowRoot.getElementById(
      this.optInPhoneConfirmId
    );
    this.phonesMatch =
      phoneField.value.trim() === confirmPhoneField.value.trim();
    if (!this.phonesMatch) {
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        { replacements: { value: 'Please make sure the phone numbers match' } }
      );
    }
    this.validity.comparePhones = this.phonesMatch;
  }
}

customElements.define('refi-text-opt-in-modal', RefiPersonalTextOptInComponent);
